import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Pagination,
  IconButton,
  TextField,
  CardActions,
  CircularProgress,
  useMediaQuery,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Stack, styled } from "@mui/system";
import {
  Search,
  BookOpen,
  Star,
  StarOff,
  FileText,
  Library,
  BookMarked,
  ChevronRight,
} from "lucide-react";
import { showInsufficientCreditsModal } from "../actions/creditActions";
import { deductCredits } from "../utils/HelperFunction/deductCredits";
import {
  legalLibrarySearchApi,
  getUserFavoritesApi,
  getPdfFromBlobStorageApi,
  removeFavoriteApi,
  addFavoriteApi,
} from "../services/api/legalLibraryApi";
import LogoLoader from "./UI/LogoLoader";

// Custom styled components
const SearchCard = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#fff",
  borderRadius: "16px",
  padding: "20px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
  border: "1px solid #F1F5F9",
  marginBottom: "24px",
});

const ResultContainer = styled(Box)(({ theme }) => ({
  padding: "24px",
  backgroundColor: "#fff",
  flexGrow: 1,
  borderRadius: "16px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
  border: "1px solid #F1F5F9",
  overflowY: "auto",
  "::-webkit-scrollbar": {
    width: "6px",
  },
  "::-webkit-scrollbar-track": {
    background: "#F8FAFC",
  },
  "::-webkit-scrollbar-thumb": {
    background: "#CBD5E1",
    borderRadius: "3px",
    "&:hover": {
      background: "#94A3B8",
    },
  },
}));

const ResultCard = styled(Card)(({ theme }) => ({
  padding: "0",
  borderRadius: "12px",
  border: "1px solid #F1F5F9",
  backgroundColor: "#FFFFFF",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    transform: "translateY(-2px)",
    borderColor: "#CBD5E1",
  },
}));

const cleanAndFormatTitle = (title) => {
  if (!title) return "";

  // Remove .pdf suffix if present
  let formattedTitle = title.replace(/\.pdf$/i, "");

  // Remove quotes (single or double)
  formattedTitle = formattedTitle.replace(/['"“”‘’]/g, "");

  // Remove leading date formats (e.g., "21.3.2023") or percentage values (e.g., "7.5%")
  formattedTitle = formattedTitle.replace(
    /^(\d{1,2}\.\d{1,2}\.\d{4}|\d+\.\d+%)\s*/,
    "",
  );

  // Remove any leading numbers (e.g., "1960_31_")
  formattedTitle = formattedTitle.replace(/^\d+[_-]?\d*[_-]?/, "");

  // Remove any text inside parentheses if it's a day of the week or a number (e.g., "(Tuesday)", "(02)")
  formattedTitle = formattedTitle.replace(
    /\(\s*(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|\d+)\s*\)/gi,
    "",
  );

  // Replace underscores and dashes with spaces
  formattedTitle = formattedTitle.replace(/[_-]/g, " ");

  // Check if the title already ends with a year preceded by a comma
  const yearMatch = formattedTitle.match(/,\s?\d{4}$/);

  // If no year at the end, find the year and add a comma before it
  if (!yearMatch) {
    formattedTitle = formattedTitle.replace(/(\d{4})$/, ", $1");
  }

  // Convert to title case, skipping the year part
  formattedTitle = formattedTitle.replace(/\b\w+/g, (word, index) => {
    // Capitalize each word except already capitalized parts or the year
    if (/\d{4}/.test(word)) {
      return word; // Don't change the year
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return formattedTitle.trim();
};

const highlightText = (text, keyword) => {
  if (!keyword) return text;
  const parts = text.split(new RegExp(`(${keyword})`, "gi"));
  return parts.map((part, index) =>
    part.toLowerCase() === keyword.toLowerCase() ? (
      <span key={`highlight-${index}`} style={{ backgroundColor: "yellow" }}>
        {part}
      </span>
    ) : (
      part
    ),
  );
};

const LegalLibrary = () => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const dispatch = useDispatch();
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);

  const [searchTerm, setSearchTerm] = useState("");
  const [confirmedSearchTerm, setConfirmedSearchTerm] = useState("");
  const [filteredStatutes, setFilteredStatutes] = useState([]);
  const [favSearchResults, setFavSearchResults] = useState([]);
  const [statuteNames, setStatuteNames] = useState([]);
  const [userFavorites, setUserFavorites] = useState([]);
  const [isStatuteNamesLoading, setIsStatuteNamesLoading] = useState(false);
  const [isFavoriteChecked, setIsFavoriteChecked] = useState(false);
  const [hasNoSearchResults, setHasNoSearchResults] = useState(false);
  const [hasNoFavorites, setHasNoFavorites] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingPdf, setLoadingPdf] = useState({});
  const [booksCount, setBooksCount] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const cardsPerPage = isMobileView ? 12 : 16;

  const totalPages = Math.ceil(
    favSearchResults?.length > 0
      ? favSearchResults.length / cardsPerPage
      : filteredStatutes.length > 0
        ? filteredStatutes.length / cardsPerPage
        : booksCount / cardsPerPage,
  );
  const indexOfLastCard = currentPage * cardsPerPage;
  const indexOfFirstCard = indexOfLastCard - cardsPerPage;
  const currentStatutes =
    favSearchResults?.length > 0
      ? favSearchResults.slice(indexOfFirstCard, indexOfLastCard)
      : filteredStatutes?.length > 0
        ? filteredStatutes.slice(indexOfFirstCard, indexOfLastCard)
        : statuteNames;

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleFavoriteFilterToggle = () => {
    const nextIsFavoriteChecked = !isFavoriteChecked;
    setIsFavoriteChecked(nextIsFavoriteChecked);
    if (nextIsFavoriteChecked) {
      fetchFavoriteStatutes();
      setCurrentPage(1);
      setSearchTerm("");
      setConfirmedSearchTerm("");
    } else {
      setHasNoFavorites(false);
      setHasNoSearchResults(false);
      setFavSearchResults([]);
      setFilteredStatutes([]);
      setSearchTerm("");
      setConfirmedSearchTerm("");
    }
  };

  const getStatuteNames = async (searchTerm) => {
    if (isFavoriteChecked) return;
    try {
      setIsStatuteNamesLoading(true);
      const requestBodyForBookRetrieval = {
        search: searchTerm,
        count: true,
        top: cardsPerPage,
        skip: (currentPage - 1) * cardsPerPage,
      };

      const statutesResponse = await legalLibrarySearchApi(
        requestBodyForBookRetrieval,
      );
      let favoritesResponse = [];

      try {
        favoritesResponse = await getUserFavoritesApi(userInfo._id);
      } catch (error) {
        favoritesResponse = [];
      }

      if (statutesResponse.status === 200) {
        const statutesData = statutesResponse.data;
        if (statutesData?.count === 0) {
          setHasNoSearchResults(true);
          return;
        }

        let favoriteTitles = [];

        if (favoritesResponse.status === 200) {
          const favoritesData = favoritesResponse.data;
          favoriteTitles = favoritesData?.map((fav) => fav);
          setUserFavorites(favoriteTitles);
        }

        const updatedStatuteData = statutesData?.result
          .filter((obj) => obj.name !== "...")
          .map((statute) => ({
            originalTitle: statute.name,
            title: cleanAndFormatTitle(statute.name),
            blob: statute.blob,
            type: statute.type,
            isFavorite: favoriteTitles.includes(statute.name),
          }))
          .sort((a, b) =>
            a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
          );
        setBooksCount(statutesData?.count);
        setStatuteNames(updatedStatuteData);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsStatuteNamesLoading(false);
    }
  };

  const fetchFavoriteStatutes = async () => {
    // Handle no favorites
    if (userFavorites?.length === 0) {
      setHasNoFavorites(true);
      return;
    }
    try {
      setIsStatuteNamesLoading(true);

      // Request body for fetching statutes
      const requestBodyForBookRetrieval = {
        search: "",
        top: -1,
        skip: 0,
        filter: { titles: userFavorites },
      };

      const statutesResponse = await legalLibrarySearchApi(
        requestBodyForBookRetrieval,
      );

      if (statutesResponse.status === 200) {
        const statutesData = statutesResponse.data;
        const updatedStatuteData = statutesData?.result
          .map((statute) => ({
            originalTitle: statute.name,
            title: cleanAndFormatTitle(statute.name),
            url: statute.url,
            type: statute.type,
            isFavorite: true,
          }))
          .sort((a, b) =>
            a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
          );
        setFilteredStatutes(updatedStatuteData);
      }
    } catch (error) {
      console.error("Error fetching favorite statutes:", error);
    } finally {
      setIsStatuteNamesLoading(false);
    }
  };

  const getBookPdf = async (blob) => {
    try {
      const response = await getPdfFromBlobStorageApi(blob);
      if (response.status !== 200) {
        throw new Error(
          `Failed to fetch PDF: ${response?.status} ${response?.statusText}`,
        );
      }
      // The backend may return the SAS URL as a raw string, a JSON-quoted
      // string (e.g. `"https://...pdf?..."`), or an object such as { url }.
      // Axios may have already parsed a JSON body, so handle all shapes.
      let url = response.data;
      if (typeof url === "string") {
        try {
          url = JSON.parse(url);
        } catch {
          // Not JSON-encoded — it's already the plain URL string.
        }
      }
      if (url && typeof url === "object") {
        url = url.url || url.sasUrl || url.data || "";
      }
      if (typeof url !== "string" || !/^https?:\/\//i.test(url)) {
        console.error("Unexpected PDF URL response shape:", response.data);
        return null;
      }
      return url;
    } catch (error) {
      console.error("Error fetching PDF:", error.message);
      return null;
    }
  };

  const toggleFavorite = async (statuteItem) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }
    const isRemovingFavorite = statuteItem.isFavorite;

    try {
      const response = isRemovingFavorite
        ? await removeFavoriteApi(userInfo._id, statuteItem.originalTitle)
        : await addFavoriteApi(userInfo._id, statuteItem.originalTitle);

      if (response.status === 200) {
        setSnackbarMessage({
          type: "success",
          text: isRemovingFavorite
            ? "Removed from Favorites"
            : "Added to Favorites!",
        });

        const updatedStatuteNames = statuteNames.map((statute) =>
          statute.originalTitle === statuteItem.originalTitle
            ? { ...statute, isFavorite: !statute.isFavorite }
            : statute,
        );
        setStatuteNames(updatedStatuteNames);
        setUserFavorites((prevFavorites) => {
          if (prevFavorites.includes(statuteItem.originalTitle)) {
            return prevFavorites.filter(
              (fav) => fav !== statuteItem.originalTitle,
            );
          } else {
            return [...prevFavorites, statuteItem.originalTitle];
          }
        });

        if (favSearchResults.length > 0) {
          const updatedFavSearchResults = favSearchResults
            .map((statute) =>
              statute.title === statuteItem.title
                ? { ...statute, isFavorite: !statute.isFavorite }
                : statute,
            )
            .filter((statute) => statute.isFavorite);
          if (updatedFavSearchResults?.length === 0 && confirmedSearchTerm) {
            setHasNoSearchResults(true);
          }
          setFavSearchResults(updatedFavSearchResults);
        }
        if (filteredStatutes?.length > 0 && !isFavoriteChecked) {
          const updatedFilteredStatutes = filteredStatutes.map((statute) =>
            statute.title === statuteItem.title
              ? { ...statute, isFavorite: !statute.isFavorite }
              : statute,
          );
          setFilteredStatutes(updatedFilteredStatutes);
        }
        if (filteredStatutes?.length > 0 && isFavoriteChecked) {
          const updatedFilteredStatutes = filteredStatutes.map((statute) =>
            statute.title === statuteItem.title
              ? { ...statute, isFavorite: !statute.isFavorite }
              : statute,
          );
          const filtered = updatedFilteredStatutes.filter(
            (statute) => statute.isFavorite,
          );
          if (filtered?.length === 0) {
            setHasNoFavorites(true);
          }
          setFilteredStatutes(filtered);
        }

        // Deduct credits using the helper
        const deductionSuccess = deductCredits(
          {
            userId,
            creditsToDeduct: 1,
            event: "Toggle Favourite in Legal Library",
          },
          dispatch,
        );

        if (!deductionSuccess) {
          console.warn(
            "Failed to deduct credits. Ensure retry or user notification.",
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleReadNow = async (obj) => {
    try {
      setLoadingPdf((prev) => ({ ...prev, [obj?.title]: true }));
      if (obj?.blob !== "") {
        const url = await getBookPdf(obj.blob);
        if (!url) {
          console.error("Failed to retrieve PDF");
          alert("Failed to retrieve the PDF. Please try again.");
          return;
        }
        // `url` is a SAS-signed Azure Blob Storage URL (e.g.
        // https://<account>.blob.core.windows.net/<container>/<file>.pdf?se=...&sp=r&sv=...&sr=b&sig=...).
        // Opening it directly can make the browser download the file instead of
        // previewing it (Azure returns it with a generic/attachment content type),
        // so fetch the bytes and open them as an explicitly-typed PDF blob instead.
        try {
          const pdfResponse = await fetch(url);
          if (!pdfResponse.ok) {
            throw new Error(`Failed to download PDF: ${pdfResponse.status}`);
          }
          const pdfBlob = await pdfResponse.blob();
          const objectUrl = URL.createObjectURL(
            new Blob([pdfBlob], { type: "application/pdf" }),
          );
          window.open(objectUrl, "_blank");
        } catch (fetchError) {
          console.error(
            "Falling back to direct URL after blob fetch failed:",
            fetchError,
          );
          window.open(url, "_blank");
        }
      } else {
        window.open(obj.url, "_blank");
      }
    } catch (error) {
      console.error("Error retrieving PDF:", error);
    } finally {
      setLoadingPdf((prev) => ({ ...prev, [obj?.title]: false }));
    }
  };

  const handleInputChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    if (e.target.value === "" && !isFavoriteChecked && confirmedSearchTerm) {
      getStatuteNames("");
      setHasNoSearchResults(false);
      setConfirmedSearchTerm("");
      setCurrentPage(1);
    }
    if (e.target.value === "" && isFavoriteChecked) {
      setFavSearchResults([]);
      setHasNoSearchResults(false);
      setConfirmedSearchTerm("");
      setCurrentPage(1);
    }
  };

  const handleSearch = async () => {
    if (searchTerm === "") return;
    setConfirmedSearchTerm(searchTerm);
    // Filter statutes based on search and favourite checkbox
    if (isFavoriteChecked) {
      const filteredFavResults = filteredStatutes.filter((statute) =>
        statute.title.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFavSearchResults(filteredFavResults);
      setHasNoSearchResults(filteredFavResults.length === 0);
      setCurrentPage(1);
    } else {
      setCurrentPage(1);
      await getStatuteNames(searchTerm);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  useEffect(() => {
    getStatuteNames(searchTerm);
  }, [currentPage]);

  return (
    <>
      <Helmet>
        <title>LawGPT - Legal Library</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#F8FAFC",
          boxSizing: "border-box",
          padding: { xs: "16px", md: "24px" },
          width: "100%",
          height: "100%",
          fontFamily:
            "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        }}
      >
        {/* Title at the top */}
        <Box
          sx={{
            marginBottom: "16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                backgroundColor: "#F0F9FF",
                borderRadius: "10px",
                color: "#0073A6",
              }}
            >
              <Library size={20} />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "24px" },
                fontWeight: "700",
                color: "#12243C",
                fontFamily:
                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              }}
            >
              Legal Library
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#64748B",
              lineHeight: "1.5",
              maxWidth: "500px",
              fontFamily:
                "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            }}
          >
            Access comprehensive legal documents, statutes, and regulations.
            Search through our extensive collection of legal resources to find
            the information you need.
          </Typography>
        </Box>

        {/* Three Cards Row */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          {/* Search Card */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              border: "1px solid #F1F5F9",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  backgroundColor: "#F0F9FF",
                  borderRadius: "8px",
                  color: "#0073A6",
                }}
              >
                <Search size={16} />
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#12243C",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Search Documents
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <TextField
                variant="outlined"
                placeholder="Search by title"
                fullWidth
                InputProps={{
                  sx: {
                    p: 0,
                    backgroundColor: "#F8FAFC",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  },
                  startAdornment: (
                    <Search
                      size={16}
                      style={{
                        color: "#64748B",
                        marginLeft: "8px",
                        marginRight: "4px",
                      }}
                    />
                  ),
                }}
                sx={{
                  flexGrow: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E2E8F0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#CBD5E1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0073A6",
                    },
                  },
                  "& input": {
                    padding: "8px 10px",
                    fontSize: "13px",
                  },
                }}
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
              />
            </Box>
          </Box>

          {/* Total Documents Card */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              border: "1px solid #F1F5F9",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                backgroundColor: "#F0F9FF",
                borderRadius: "8px",
                color: "#0073A6",
              }}
            >
              <FileText size={18} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#12243C",
                  marginBottom: "2px",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {favSearchResults?.length > 0
                  ? favSearchResults.length.toLocaleString()
                  : filteredStatutes?.length > 0
                    ? filteredStatutes?.length.toLocaleString()
                    : booksCount?.toLocaleString()}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#64748B",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {filteredStatutes?.length > 0 || favSearchResults?.length > 0
                  ? "Documents Found"
                  : "Total Documents"}
              </Typography>
            </Box>
          </Box>

          {/* Favorite Documents Card */}
          <Box
            onClick={handleFavoriteFilterToggle}
            sx={{
              flex: 1,
              backgroundColor: isFavoriteChecked ? "#FFFBEB" : "#FFFFFF",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              border: isFavoriteChecked
                ? "1px solid #F59E0B"
                : "1px solid #F1F5F9",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "12px",
              cursor: "pointer",
              transition:
                "background-color 0.15s ease, border-color 0.15s ease",
              "&:hover": {
                borderColor: "#F59E0B",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                backgroundColor: "#FEF3C7",
                borderRadius: "8px",
                color: "#F59E0B",
              }}
            >
              <Star size={18} fill={isFavoriteChecked ? "#F59E0B" : "none"} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#12243C",
                  marginBottom: "2px",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {userFavorites?.length || 0}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#64748B",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Favorites
              </Typography>
            </Box>
          </Box>
        </Box>

        <React.Fragment>
          {isStatuteNamesLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
              }}
            >
              <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
            </Box>
          ) : (
            <>
              <ResultContainer>
                {hasNoSearchResults || hasNoFavorites ? (
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      height: "100%",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "60px 24px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "80px",
                        height: "80px",
                        backgroundColor: hasNoSearchResults
                          ? "#F0F9FF"
                          : "#FEF3C7",
                        borderRadius: "16px",
                        marginBottom: "24px",
                        color: hasNoSearchResults ? "#0073A6" : "#F59E0B",
                      }}
                    >
                      {hasNoSearchResults ? (
                        <Search size={32} />
                      ) : (
                        <Star size={32} />
                      )}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: "20px",
                        fontWeight: "600",
                        color: "#12243C",
                        marginBottom: "8px",
                        textAlign: "center",
                        fontFamily:
                          "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      {hasNoSearchResults
                        ? "No Results Found"
                        : "No Favorites Added"}
                    </Typography>
                    <Typography
                      variant="body1"
                      textAlign="center"
                      sx={{
                        fontSize: "14px",
                        color: "#64748B",
                        maxWidth: "400px",
                        lineHeight: "1.6",
                        fontFamily:
                          "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      {hasNoSearchResults
                        ? "Please review your search term or try using broader keywords to find relevant documents."
                        : "Mark important documents as favorites for quick access later."}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0",
                    }}
                  >
                    {/* Table Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: "#F8FAFC",
                        borderBottom: "1px solid #E2E8F0",
                        borderRadius: "12px 12px 0 0",
                        borderTop: "1px solid #E2E8F0",
                        borderLeft: "1px solid #E2E8F0",
                        borderRight: "1px solid #E2E8F0",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#F0F9FF",
                            borderRadius: "8px",
                            color: "#0073A6",
                          }}
                        >
                          <FileText size={16} />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#475569",
                            fontFamily:
                              "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                          }}
                        >
                          Document Name
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#475569",
                          width: "200px",
                          textAlign: "center",
                          fontFamily:
                            "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                        }}
                      >
                        Actions
                      </Typography>
                    </Box>

                    {/* Document Rows */}
                    {currentStatutes?.map((statute, index) => (
                      <Box
                        key={`${statute.id}-${index} `}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          padding: "16px 20px",
                          backgroundColor: "#FFFFFF",
                          borderBottom: "1px solid #E2E8F0",
                          borderLeft: "1px solid #E2E8F0",
                          borderRight: "1px solid #E2E8F0",
                          transition: "all 0.2s ease",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "#F8FAFC",
                          },
                          "&:last-child": {
                            borderRadius: "0 0 12px 12px",
                          },
                        }}
                        onClick={() => handleReadNow(statute)}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                backgroundColor: "#F0F9FF",
                                borderRadius: "8px",
                                color: "#0073A6",
                              }}
                            >
                              <BookOpen size={16} />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#12243C",
                                flex: 1,
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              }}
                            >
                              {highlightText(
                                statute.title,
                                confirmedSearchTerm,
                              )}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            // width: `${isMobileView ? "200px" : "300px"}`,
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "8px 12px",
                              borderRadius: "8px",
                              backgroundColor: statute.isFavorite
                                ? "#FEF3C7"
                                : "#F8FAFC",
                              color: statute.isFavorite ? "#F59E0B" : "#64748B",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: statute.isFavorite
                                  ? "#FDE68A"
                                  : "#F1F5F9",
                              },
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              toggleFavorite(statute);
                            }}
                          >
                            {statute.isFavorite ? (
                              <Star size={16} />
                            ) : (
                              <StarOff size={16} />
                            )}
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: "500",
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              }}
                            >
                              {statute.isFavorite ? "Favorited" : "Favorite"}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Row-end open indicator */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "32px",
                            height: "32px",
                            flexShrink: 0,
                            // marginLeft: "8px",
                            color: "#0073A6",
                          }}
                        >
                          {loadingPdf[statute?.title] ? (
                            <CircularProgress
                              size={18}
                              sx={{ color: "#0073A6" }}
                            />
                          ) : (
                            <ChevronRight size={18} />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </ResultContainer>
            </>
          )}
          {statuteNames.length > 0 &&
            !hasNoSearchResults &&
            !hasNoFavorites &&
            !isStatuteNamesLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "24px",
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontSize: "14px",
                      minWidth: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      fontFamily:
                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      "&:hover": {
                        color: "#0073A6",
                        backgroundColor: "#F0F9FF",
                      },
                      "&.Mui-selected": {
                        color: "#FFFFFF",
                        backgroundColor: "#0073A6",
                        "&:hover": {
                          backgroundColor: "#005A8B",
                        },
                      },
                    },
                  }}
                />
              </Box>
            )}
        </React.Fragment>
        {snackbarMessage && (
          <Snackbar
            open={!!snackbarMessage}
            autoHideDuration={6000}
            onClose={() => setSnackbarMessage(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbarMessage(null)}
              severity={snackbarMessage.type}
              sx={{ width: "100%" }}
            >
              {snackbarMessage.text}
            </Alert>
          </Snackbar>
        )}
      </Box>
    </>
  );
};

export default LegalLibrary;
